import React, { useRef, useEffect, useMemo, useState } from 'react';
import AgGridReact from '../../../common/AgGrid/AgGridReact.jsx';
import { gridTheme } from '../../../common/AgGrid/gridTheme.js';
import { ClientSideRowModelModule } from 'ag-grid-community';
import { useSelector } from 'react-redux';
import { buildColumnDefs } from '../../../datagrid/hooks/useDynamicCols.jsx';
import LoadingIndicator from '../../../common/loading_indicator.jsx';
import { useDatasource } from '../../hooks/useDatasource.js';
import {
  selectDatasourceCols,
  selectDatasourceRows,
  selectCombinedFilterModel,
} from '../../redux/datasourceSlice.js';
import { isEqual } from 'lodash';
import AutoGroupValueRenderer from '../../../datagrid/AutoGroupValueRenderer.jsx';
import { cloneDeep } from 'lodash';

/**
 * DatasourceTable — shared AG Grid display component for datasource data.
 *
 * Props:
 *   widgetId        — optional; used to resolve datasourceId from widget state and for live refresh
 *   datasourceId    — optional override; takes precedence over the widget-state lookup
 *   localDatasource — if provided, triggers a live refetch when it changes (edit preview)
 *   live            — enables live data fetch via useDatasource
 *   applyFilter     — if true, applies the persisted filter model to the grid on mount/resize
 *   getMainMenuItems — optional AG Grid context menu builder (passed from parent for header rename)
 *   headerOverlay   — optional JSX rendered above the grid (passed from parent for header rename UI)
 *   gridProps       — additional AG Grid props (e.g. enableAdvancedFilter, onFilterChanged)
 */
function DatasourceTable({
    gridRef = null,
    localDatasource,
    widgetId,
    datasourceId: datasourceIdProp,
    opts = {},
    live = false,
    minHeight = '0px',
    containerHeight = '100%',
    gridProps = {},
    applyFilter = false,
    getMainMenuItems: getMainMenuItemsProp,
    headerOverlay,
    ...rest
}) {
    const widgetDatasourceId = useSelector(
        state => state.dashboard.widgets[widgetId]?.data_datasource?.id
    );
    const datasourceId = datasourceIdProp ?? widgetDatasourceId;
    const columns = useSelector(selectDatasourceCols(datasourceId, widgetId));
    const rows = cloneDeep(useSelector(selectDatasourceRows(datasourceId, widgetId)));
    const filterModel = useSelector(selectCombinedFilterModel(widgetId, datasourceId));
    const layout = useSelector(state => state.dashboard.layout[widgetId]);
    const [columnDefs, setColumnDefs] = useState([]);
    const { refetch } = useDatasource({ widgetId, initialDatasource: localDatasource, ...opts, live });
    gridRef ||= useRef(null);

    useEffect(() => {
        if (localDatasource) {
            gridRef.current?.api?.setGridOption('loading', true);
            refetch();
            gridRef.current?.api?.setGridOption('loading', false);
        }
    }, [localDatasource]);

    useEffect(() => {
        resizeGrid();
    }, [layout, columnDefs]);

    useEffect(() => {
        if (columns) {
            const newColDefs = buildColumnDefs(columns);
            if (!isEqual(columnDefs, newColDefs)) {
                setColumnDefs(newColDefs);
            }
        }
    }, [columns]);

    const loadingOverlayComponent = useMemo(() => LoadingIndicator, []);

    const resizeGrid = () => {
        if (gridRef.current?.api) {
            gridRef.current.api.sizeColumnsToFit();
            if (applyFilter) {
                gridRef.current.api.setAdvancedFilterModel(filterModel ?? null);
            }
        }
    };

    const autoGroupColumnDef = useMemo(() => ({
        width: 180,
        cellRendererParams: { innerRenderer: AutoGroupValueRenderer },
    }), []);

    return (
        <div style={{ width: '100%', height: containerHeight, position: 'relative' }} {...rest}>
            <div
                className='grid-theme mb-1 datasource-table'
                style={{
                    width: '100%',
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    height: '100%',
                    minHeight,
                }}
            >
                <AgGridReact theme={gridTheme}
                    groupHeaderHeight={20}
                    ref={gridRef}
                    columnDefs={columnDefs}
                    rowData={rows}
                    onGridReady={params => {
                        params.api.sizeColumnsToFit();
                        gridProps.onGridReady?.(params);
                    }}
                    loadingOverlayComponent={loadingOverlayComponent}
                    modules={[ClientSideRowModelModule]}
                    getMainMenuItems={getMainMenuItemsProp}
                    onGridSizeChanged={resizeGrid}
                    groupDisplayType='multipleColumns'
                    autoGroupColumnDef={autoGroupColumnDef}
                    {...gridProps}
                />
            </div>
            {headerOverlay}
        </div>
    );
}

export default DatasourceTable;
